import { useCallback, useState, useMemo } from "react";
import { IGif, ITag, IErrorData } from "@/common/services/types";
import { useUpdateGifMutation } from "@/common/queries/queries";
import { showNotification } from "@/components/alerts/Notifications";
import { AxiosError } from "axios";

type TUseManageGifForm = {
  gif: IGif;
  onClosePopup: () => void;
}

const MIN_TAGS = 3;
const MAX_TAGS = 10;

export const useManageGifForm = ({ gif, onClosePopup }: TUseManageGifForm) => {
  const [sexuality, setSexuality] = useState<string>(gif.sexuality?.[0] as string);
  const [description, setDescription] = useState<string>(gif.description || "");
  const [privacy, setPrivacy] = useState<boolean>(!gif.published);
  const [tags, setTag] = useState<string[]>(gif.tags || []);

  const updateGifMutation = useUpdateGifMutation();

  const onPrivacyChange = useCallback(() => {
    setPrivacy((prev) => !prev);
  }, []);

  const onPreferenceChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSexuality(event.target.value);
  }, []);

  const onDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  const onTagsChange = useCallback((tags: ITag[]) => {
    const tagNames = tags.map(tag => tag.name);
    setTag(tagNames);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateGifMutation.mutateAsync({
        id: gif.id,
        gifParams: {
          published: !privacy,
          sexuality,
          description,
          tags
        }
      });

      onClosePopup();

      showNotification({
        title: "Successfully Updated!",
        variant: "success",
      });
    } catch (e) {
      showNotification({
        title: (e as AxiosError<IErrorData>).response?.data?.error?.message ||
        "Could not update gif, please try again later",
        variant: "error",
      });
    }
  };

  const btnState = useMemo(() => tags.length < MIN_TAGS || tags.length > MAX_TAGS, [tags]);
  const isMoreThanTen = useMemo(() => tags.length > MAX_TAGS, [tags]);

  return {
    sexuality,
    description,
    privacy,
    tags,
    btnState,
    isMoreThanTen,
    onPrivacyChange,
    onPreferenceChange,
    onDescriptionChange,
    onTagsChange,
    onSubmit
  };
};
